import { useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Bottone } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'
import { decidiConferma, testoConferma } from '../../servizi/posta-messaggio.ts'
import '../../stile-posta-messaggio.css'

/**
 * Conferma di lettura (539, RFC 8098) nel messaggio aperto.
 *
 * Arrivato con la richiesta: «Il mittente chiede una conferma di lettura», con «Invia la conferma» e «Ignora».
 * Non parte mai da sola. La fascia c'e' solo se l'API la dice proponibile: chi guarda scrive dalla casella, non
 * impersona, il messaggio non e' in «Indesiderata» e nessuno ha ancora scelto. La scelta vale per il messaggio,
 * anche per i colleghi di una casella condivisa, e la fascia non torna.
 *
 * Inviato con la richiesta: sotto i comandi «Conferma di lettura chiesta», e chi l'ha mandata e quando.
 */

const quando = (iso: string) => posta.dataEstesa(iso)

export function AvvisoConferma({ messaggio }: { messaggio: posta.Messaggio }) {
  const conferma = messaggio.conferma_lettura
  const [esito, setEsito] = useState<'inviata' | 'ignorata' | null>(conferma?.esito ?? null)
  const [inCorso, setInCorso] = useState<'invia' | 'ignora' | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  if (!conferma) return null

  async function scegli(invia: boolean) {
    setInCorso(invia ? 'invia' : 'ignora'); setErrore(null)
    try {
      setEsito((await decidiConferma(messaggio.id, invia)).esito)
    } catch (e) {
      // Un collega ha gia' scelto: la fascia sparisce come se avesse scelto qui.
      if (e instanceof ErroreApi && e.codice === 'CONFERMA_GIA_DECISA') setEsito('ignorata')
      else setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
    } finally { setInCorso(null) }
  }

  if (esito === 'inviata') {
    return (
      <div className="posta-conferme-ricevute" role="status">
        <span>Conferma di lettura inviata{conferma.decisa_il ? ` il ${quando(conferma.decisa_il)}` : ''}.</span>
      </div>
    )
  }
  if (esito || !conferma.proponibile) return null
  const mittente = posta.soloIndirizzo(messaggio.da).toLowerCase()
  return (
    <div className="posta-conferma" role="note">
      <span className="posta-conferma-testo">
        <strong>Il mittente chiede una conferma di lettura.</strong>
        {' '}Dice solo che hai aperto il messaggio; non parte se non la mandi tu.
        {conferma.a !== mittente && <> La conferma andrebbe a <span className="mono">{conferma.a}</span>, che non è il mittente.</>}
      </span>
      <span className="posta-conferma-comandi">
        <Bottone dimensione="piccolo" variante="primario" inCorso={inCorso === 'invia'} disabled={!!inCorso}
                 onClick={() => void scegli(true)}>Invia la conferma</Bottone>
        <Bottone dimensione="piccolo" variante="discreto" inCorso={inCorso === 'ignora'} disabled={!!inCorso}
                 onClick={() => void scegli(false)}>Ignora</Bottone>
      </span>
      {errore && <span className="posta-conferma-errore" role="alert">{errore}</span>}
    </div>
  )
}

/** Sul messaggio inviato: la conferma chiesta e quelle arrivate («Letto da X il …»). */
export function ConfermeRicevute({ messaggio }: { messaggio: posta.Messaggio }) {
  const c = messaggio.conferme_ricevute
  if (!c || (!c.chiesta && !c.ricevute.length)) return null
  return (
    <div className="posta-conferme-ricevute">
      {c.ricevute.map((r, i) => (
        <span key={i}>{testoConferma(r.disposizione)} {r.lettore} il {quando(r.ricevuta_il)}.</span>
      ))}
      {c.chiesta && !c.ricevute.length && (
        <span className="tenue">Conferma di lettura chiesta: ancora nessuna risposta. Chi riceve può non mandarla.</span>
      )}
    </div>
  )
}
